// Dates are stored as much of YYYY-MM-DD as is actually known: "1950" and
// "1950-03" are as legitimate as a full date, and GEDCOM import produces plenty
// of both. Rendering assumed all three parts were there, which turned a
// year-only date into "undefined NaN, 1950".
export function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = String(d).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = m ? months[+m - 1] : null;
  if (!month) return y || '—';
  if (!day) return `${month} ${y}`;
  return `${month} ${+day}, ${y}`;
}

export function getAge(birth, death) {
  if (!birth) return null;
  const b = new Date(birth);
  const end = death ? new Date(death) : new Date();
  let age = end.getFullYear() - b.getFullYear();
  if (end.getMonth() < b.getMonth() ||
    (end.getMonth() === b.getMonth() && end.getDate() < b.getDate())) age--;
  return age;
}

export function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export function ordinal(n) {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

const CURRENCY_SYMBOLS = { USD: '$', CAD: '$', AUD: '$', NZD: '$', EUR: '€', GBP: '£', JPY: '¥' };

export function formatPrice(price, currency) {
  if (!price) return null;
  const symbol = CURRENCY_SYMBOLS[currency];
  return symbol ? `${symbol}${price}` : `${price} ${currency}`;
}

export function formatPlanPrice(price, currency) {
  const formatted = formatPrice(price, currency);
  return formatted ? `${formatted}/month` : null;
}

// Default name for a newly-created tree — derived from the owner's own name
// so it reads as theirs from the start, rather than the generic "My Family
// Tree" every account used to get. Assumes "First Last" order (a reasonable
// default, not universally correct) and takes the last word as the surname;
// falls back to a possessive form for a single-word name.
export function defaultTreeName(displayName) {
  const parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'My Family Tree';
  if (parts.length === 1) return `${parts[0]}'s Family Tree`;
  return `${parts[parts.length - 1]} Family Tree`;
}

/**
 * Whether a freshly signed-in user should be given a personal tree.
 *
 * Extracted from TreeContext's bootstrap because getting it wrong is not
 * visible in the code that surrounds it. It used to ask whether the user *owned*
 * a tree, which is false for every invited viewer — so being invited to a family
 * tree also minted an empty one of your own, and (see chooseActiveTree) dropped
 * you into it. Someone joining a tree is not asking to start one.
 */
export function needsStarterTree(trees) {
  return (trees || []).length === 0;
}

/**
 * Which tree to open on arrival.
 *
 * Owned trees win because that is the common case — but the fallback matters
 * more than the rule: a user with only shared trees must land in one of them,
 * not in nothing. Returns null for an empty list so the caller has to think
 * about it rather than reading `.treeId` off undefined.
 */
export function chooseActiveTree(trees, preferredTreeId = null) {
  const list = trees || [];
  // An explicit preference beats the general rule. It exists for one case: the
  // user followed an invitation link, and the tree it was for is the only
  // answer they will accept — landing them in their own tree instead is the
  // flow ending on the wrong screen.
  if (preferredTreeId) {
    const wanted = list.find(t => t.treeId === preferredTreeId);
    if (wanted) return wanted;
  }
  return list.find(t => t.role === 'owner') || list[0] || null;
}

/**
 * Orders people by name for anything a human reads down.
 *
 * Shared because it had already drifted: two of the three person pickers sorted
 * and the third did not, so choosing a mother or father meant scanning a list
 * in whatever order Firestore returned — which is roughly the order people were
 * added, and on a large tree is no order at all.
 *
 * localeCompare rather than `<`, so accented names land next to their
 * unaccented neighbours instead of after Z. Sorting on `name || ''` keeps
 * people with no name at the top, which is where the existing pickers already
 * put them; they are usually placeholders someone is partway through filling in
 * and worth seeing rather than burying.
 *
 * Sort a copy — Object.values() is safe to sort in place, a prop or state array
 * is not.
 */
export function byName(a, b) {
  return (a?.name || '').localeCompare(b?.name || '');
}

/**
 * Every marriage a person is part of, read from both sides.
 *
 * A marriage is stored twice, once in each spouse's own `marriages` array, and
 * the two add-person paths mirror it in a second write that is not batched with
 * the first — so an interrupted add leaves the record on one document only.
 * Readers that only consult `person.marriages` then disagree with each other:
 * the couple drew on the canvas and listed on the Relationships tab (both of
 * which scan everyone) while the life timeline showed the marriage on one
 * spouse and not the other.
 *
 * Own entries come first because they carry the most detail; the reverse pass
 * fills in only spouses not already seen, mirrored so the entry reads from this
 * person's perspective.
 *
 * Entries with no `spouseId` are dropped — they name no one, so there is no
 * second side to reconcile and nothing for a spouse-keyed caller to do with
 * them. A caller that renders a bare marriage date (the timeline does) has to
 * pick those out of `person.marriages` itself.
 */
export function deriveMarriages(personId, people) {
  const person = (people || {})[personId];
  if (!person) return [];

  const seen = new Set();
  const result = [];

  (person.marriages || []).forEach(m => {
    if (!m.spouseId) return;
    seen.add(m.spouseId);
    result.push(m);
  });

  Object.values(people).forEach(other => {
    if (other.id === personId) return;
    (other.marriages || []).forEach(m => {
      if (m.spouseId !== personId || seen.has(other.id)) return;
      seen.add(other.id);
      result.push({
        spouseId: other.id,
        marriageDate: m.marriageDate || null,
        marriagePlace: m.marriagePlace || null,
        status: m.status,
        endReason: m.endReason || null,
        endDate: m.endDate || null,
        children: m.children || [],
      });
    });
  });

  return result;
}
